import DateTimeBase from "./date_time_base_controller";
import flatpickr from "flatpickr";
import { slotLabels, normalizeTime, minutesFromLabel, ALLOWED_TIME_CHARS } from "./helpers/time_combobox";

// Connects to data-controller="time-input"
//
// Two render modes:
//   default  — flatpickr time spinner (h:i K, 30-min steps).
//   combobox — a 30-minute-slot dropdown (data-time-input-combobox-value="true"),
//              matching the onboarding picker. Used by the event create/edit form.
//
// Both modes keep the same controller interface (timeInputTarget,
// startTime/endTime/type values, updateEndTimeMin/validateEndTime) so the shared
// date-input orchestration works unchanged. In combobox mode a no-op _flatpickr
// shim is installed on the input so date-input's `.set("minTime", …)` calls are
// harmless, and the end-after-start constraint simply CLEARS an invalid end time
// (server-side end_must_be_after_start is the backstop).
export default class extends DateTimeBase {
  static targets = ["timeInput", "hiddenTimeInput", "menu"];
  static values = {
    startTime: Boolean,
    endTime: Boolean,
    type: String,
    stopScroll: Boolean,
    combobox: Boolean,
    // When true, the block may cross midnight, so an end clock at/before the
    // start clock is valid (no minTime constraint, no clearing). Used by the
    // sub-event edit form, where each occurrence can run overnight (e.g. a
    // festival night 1 PM → 9:30 AM) and the server rolls the end to next day.
    allowOvernight: Boolean
  };

  connect() {
    if (this.comboboxValue) {
      this.initCombobox();
      return;
    }

    const modal = this.timeInputTarget.closest('.modal');

    const flatpickrConfig = {
      enableTime: true,
      noCalendar: true,
      time_24hr: false,
      dateFormat: "h:i K",
      minuteIncrement: 30, // step the time picker in 30-minute increments
      onChange: this.handleTimeChange.bind(this),
      static: false,
    };

    // Only apply scroll prevention if stopScrollValue is true
    if (this.stopScrollValue) this.stopAutoScroll(flatpickrConfig, modal)

    flatpickr(this.timeInputTarget, flatpickrConfig);
  }

  // ---- Combobox mode -------------------------------------------------------

  initCombobox() {
    // Shim so date-input's `endTimeInput.timeInputTarget._flatpickr.set(...)`
    // calls no-op instead of throwing in combobox mode.
    this.timeInputTarget._flatpickr = { set: () => {} };

    this.buildComboboxOptions();

    this._onDocClick = (e) => { if (!this.element.contains(e.target)) this.closeMenu(); };
    document.addEventListener("click", this._onDocClick);

    this._onFocus = () => this.openMenu();
    this._onClick = () => this.openMenu();
    this._onInput = () => { this.openMenu(); this.highlightMatch(); };
    this._onBlur = () => this.onComboboxBlur();
    this._onKeydown = (e) => this.filterKeydown(e);
    this._onBeforeInput = (e) => this.filterBeforeInput(e);
    this.timeInputTarget.addEventListener("focus", this._onFocus);
    this.timeInputTarget.addEventListener("click", this._onClick);
    this.timeInputTarget.addEventListener("input", this._onInput);
    this.timeInputTarget.addEventListener("blur", this._onBlur);
    this.timeInputTarget.addEventListener("keydown", this._onKeydown);
    this.timeInputTarget.addEventListener("beforeinput", this._onBeforeInput);
  }

  buildComboboxOptions() {
    if (!this.hasMenuTarget) return;
    const frag = document.createDocumentFragment();
    for (const label of slotLabels({ words: false })) {
      const li = document.createElement("li");
      li.className = "time-select-option";
      li.textContent = label;
      li.dataset.value = label;
      li.setAttribute("role", "option");
      li.addEventListener("mousedown", (e) => {
        e.preventDefault(); // fire before the input's blur
        this.selectOption(label);
      });
      frag.appendChild(li);
    }
    this.menuTarget.replaceChildren(frag);
  }

  filterBeforeInput(e) {
    if (e.data && !ALLOWED_TIME_CHARS.test(e.data)) e.preventDefault();
  }

  filterKeydown(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const allowedKeys = ["Backspace", "Delete", "Tab", "Enter", "Escape",
                         "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    if (allowedKeys.includes(e.key)) return;
    if (e.key.length === 1 && !ALLOWED_TIME_CHARS.test(e.key)) e.preventDefault();
  }

  openMenu() {
    if (!this.hasMenuTarget) return;
    this.menuTarget.classList.add("is-open");
    this.scrollToSelected();
  }

  closeMenu() {
    if (this.hasMenuTarget) this.menuTarget.classList.remove("is-open");
  }

  selectOption(label) {
    this.timeInputTarget.value = label;
    this.closeMenu();
    this.runComboboxConstraint();
    this.timeInputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  onComboboxBlur() {
    setTimeout(() => this.closeMenu(), 120); // let an option mousedown land first
    const normalized = normalizeTime(this.timeInputTarget.value, { words: false });
    this.timeInputTarget.value = normalized || "";
    if (normalized) {
      this.runComboboxConstraint();
      this.timeInputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  // Flatpickr fires onChange → handleTimeChange; the combobox has no onChange, so
  // run the same start/end constraint manually after a select/blur.
  runComboboxConstraint() {
    if (this.startTimeValue) {
      this.updateEndTimeMin(this.timeInputTarget.value);
    } else if (this.endTimeValue) {
      this.validateEndTime(this.timeInputTarget.value);
    }
  }

  highlightMatch() {
    if (!this.hasMenuTarget) return;
    const val = this.timeInputTarget.value.trim().toLowerCase();
    let match = null;
    this.menuTarget.querySelectorAll(".time-select-option").forEach((li) => {
      const on = val && li.dataset.value.toLowerCase().startsWith(val);
      li.classList.toggle("is-active", !!on);
      if (on && !match) match = li;
    });
    if (match) match.scrollIntoView({ block: "nearest" });
  }

  scrollToSelected() {
    if (!this.hasMenuTarget) return;
    const val = this.timeInputTarget.value.trim();
    const sel = [...this.menuTarget.querySelectorAll(".time-select-option")]
      .find((li) => li.dataset.value === val);
    if (sel) sel.scrollIntoView({ block: "center" });
  }

  // ---- flatpickr mode (scroll prevention in modals) ------------------------

  stopAutoScroll(flatpickrConfig, modal){
    const originalScrollIntoView = this.timeInputTarget.scrollIntoView;
    this.timeInputTarget.scrollIntoView = function() {};

    flatpickrConfig.appendTo = modal || document.body;

    flatpickrConfig.onReady = (selectedDates, dateStr, instance) => {
      if (instance.calendarContainer) instance.calendarContainer.scrollIntoView = function() {};
    };

    flatpickrConfig.onOpen = (selectedDates, dateStr, instance) => {
      if (instance.calendarContainer) {
        const inputRect = this.timeInputTarget.getBoundingClientRect();
        const calendar = instance.calendarContainer;

        calendar.style.position = 'fixed';
        calendar.style.top = `${inputRect.bottom + 2}px`;
        calendar.style.left = `${inputRect.left}px`;
        calendar.style.zIndex = '9999';
      }
    };

    this.cleanup = () => {
      this.timeInputTarget.scrollIntoView = originalScrollIntoView;
    };
  }

  disconnect() {
    if (this.cleanup) this.cleanup();
    if (this._onDocClick) document.removeEventListener("click", this._onDocClick);
  }

  // ---- shared cross-field logic (called by date-input and own onChange) ----

  handleTimeChange(selectedDates, dateStr) {
    const selectedTime = this.convertTo24HourFormat(dateStr);

    if (this.startTimeValue) {
      this.updateEndTimeMin(selectedTime);
    } else if (this.endTimeValue) {
      this.validateEndTime(selectedTime);
    }
  }

  updateEndTimeMin(startTime) {
    const endTimeInput = this.getEndTimeInput(this.typeValue);
    if (!endTimeInput) return;

    // Overnight-capable block (sub-event): the end may cross midnight, so impose
    // no constraint on the end clock.
    const overnightOk = this.allowOvernightValue || endTimeInput.allowOvernightValue;

    // Combobox end field: no flatpickr minTime — just clear it if now invalid.
    // `startTime` may arrive as 24h ("13:00", from date-input) or a 12h label
    // ("1:00 PM", from the combobox itself), so accept either. The end clock must
    // be after the start clock only when the block stays within ONE day; when the
    // end date is later than the start date the block crosses midnight (e.g. a
    // festival night 8 PM → 2 AM), so any end time is valid.
    if (endTimeInput.comboboxValue) {
      if (overnightOk || this.endsOnLaterDate()) return;
      const startMin = minutesFromLabel(startTime) ?? this.toMinutes(startTime);
      const endMin = minutesFromLabel(endTimeInput.timeInputTarget.value);
      if (startMin != null && endMin != null && endMin <= startMin) {
        endTimeInput.timeInputTarget.value = "";
      }
      return;
    }

    const endFlatpickrInstance = endTimeInput.timeInputTarget._flatpickr;
    if (endFlatpickrInstance) {
      // Overnight-capable: don't restrict selectable end slots to after the start.
      endFlatpickrInstance.set("minTime", overnightOk ? null : startTime);
      if (overnightOk) return;

      // Clear invalid end time
      const endTime = this.convertTo24HourFormat(endTimeInput.timeInputTarget.value);
      if (endTime && startTime > endTime) {
        endTimeInput.timeInputTarget.value = "";
      }
    }
  }

  validateEndTime(endTime) {
    // Overnight-capable block (sub-event): the end may cross midnight, so accept
    // any end time as-is.
    if (this.allowOvernightValue) return;

    // Combobox (event form): the end clock must be after the start clock only
    // within a SINGLE day. When the end date is later than the start date the
    // block runs past midnight (e.g. 8 PM → 2 AM), so any end time is valid —
    // leave it alone. Mirrors the flatpickr path's endsOnLaterDate() handling.
    if (this.comboboxValue) {
      if (this.endsOnLaterDate()) return;
      const startTimeInput = this.getStartTimeInput(this.typeValue);
      if (!startTimeInput) return;
      const startMin = minutesFromLabel(startTimeInput.timeInputTarget.value);
      const endMin = minutesFromLabel(this.timeInputTarget.value);
      if (startMin != null && endMin != null && endMin <= startMin) {
        this.timeInputTarget.value = "";
      }
      return;
    }

    // flatpickr path (other forms) — unchanged date-aware behavior.
    if (this.endsOnLaterDate()) {
      const endFlatpickr = this.getEndTimeInput(this.typeValue).timeInputTarget._flatpickr;
      if (endFlatpickr) endFlatpickr.set("minTime", null);
    } else {
      const startTimeInput = this.getStartTimeInput(this.typeValue);
      if (!startTimeInput) return;
      const startTime = this.convertTo24HourFormat(startTimeInput.timeInputTarget.value);
      if (startTime && parseInt(endTime.split(':')[0]) < parseInt(startTime.split(':')[0])) {
        this.timeInputTarget.value = "";
      }
    }
  }

  // "13:30" / "9:00" (24-hour) → minutes since midnight.
  toMinutes(time24) {
    if (!time24) return null;
    const [h, m] = String(time24).split(":").map((n) => parseInt(n, 10));
    if (isNaN(h)) return null;
    return h * 60 + (isNaN(m) ? 0 : m);
  }

  // True when this field's type has an end-date input whose date is LATER than
  // the start date (multi-day / recurring). Then the clock order is irrelevant.
  // Single-day events have no end-date input, so this is always false for them.
  endsOnLaterDate() {
    if (this.typeValue === '') return false;
    const endDateInput = this.getEndDateInput(this.typeValue);
    const startDateInput = this.getStartDateInput(this.typeValue);
    if (!endDateInput || !startDateInput) return false;
    const end = endDateInput.dateInputTarget.value;
    const start = startDateInput.dateInputTarget.value;
    if (!end || !start) return false;
    return new Date(end) > new Date(start);
  }
}
